import { zodResolver } from '@hookform/resolvers/zod';
import { Info, LogIn, User } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useActivateAdminUser } from '../hooks/useActivateAdminUser';
import { useAdminUserDetail } from '../hooks/useAdminUserDetail';
import { useChangeAdminUserRole } from '../hooks/useChangeAdminUserRole';
import { useDeactivateAdminUser } from '../hooks/useDeactivateAdminUser';
import { useResendInvitation } from '../hooks/useResendInvitation';
import { useUpdateAdminUser } from '../hooks/useUpdateAdminUser';
import {
  updateAdminUserSchema,
  type UpdateAdminUserFormValues,
} from '../schemas/admin-user-schema';
import { type AdminUserListDto } from '../types/admin-user.types';
import { AdminUserInfoTab } from './AdminUserInfoTab';
import { AdminUserLoginAttemptsTab } from './AdminUserLoginAttemptsTab';
import { ResendInvitationModal } from './ResendInvitationModal';
import { ToggleStatusModal } from './ToggleStatusModal';
import { useAuthStore } from '@/core/stores/authStore';
import { useRoles } from '@/features/roles/hooks/useRoles';
import { ConfirmDialog } from '@/shared/components/ahkinia/ConfirmDialog/ConfirmDialog';
import { Notification } from '@/shared/components/ahkinia/Notification/notification';
import { SelectInput, SelectItem } from '@/shared/components/ahkinia/Select/select';
import { ModalForm } from '@/shared/components/ui/modal-form';
import { UnifiedButton } from '@/shared/components/ui/unified-button';
import { cn } from '@/shared/lib/cn';
import { getRoleLabel } from '@/shared/utils/role-label.utils';

interface AdminUserDetailModalProps {
  user: AdminUserListDto | null;
  onClose: () => void;
}

type DetailTab = 'info' | 'login-attempts';

const DETAIL_TABS: { key: DetailTab; label: string; icon: typeof Info }[] = [
  { key: 'info', label: 'Información', icon: Info },
  { key: 'login-attempts', label: 'Historial de accesos', icon: LogIn },
];

export function AdminUserDetailModal({ user, onClose }: AdminUserDetailModalProps) {
  // react-doctor-disable-next-line react-doctor/no-event-handler -- RHF form.reset on async-loaded detail / controlled-modal prop sync; not derivable in an event handler
  const { data: detail, isLoading, isError } = useAdminUserDetail(user?.id ?? null);
  const { data: rolesData, isLoading: rolesLoading } = useRoles({ pageSize: 100 });
  const currentUserId = useAuthStore((s) => s.user?.userId);

  const [activeTab, setActiveTab] = useState<DetailTab>('info');
  const [isEditing, setIsEditing] = useState(false);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [pendingEditValues, setPendingEditValues] = useState<UpdateAdminUserFormValues | null>(
    null,
  );
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);

  const { mutate: updateUser, isPending: isUpdating } = useUpdateAdminUser();
  const { mutate: changeRole, isPending: isChangingRole } = useChangeAdminUserRole();
  const { mutate: activate, isPending: activatePending } = useActivateAdminUser();
  const { mutate: deactivate, isPending: deactivatePending } = useDeactivateAdminUser();
  const { mutate: resend, isPending: resendPending } = useResendInvitation();

  const togglePending = activatePending || deactivatePending;

  const form = useForm<UpdateAdminUserFormValues>({
    resolver: zodResolver(updateAdminUserSchema),
    defaultValues: { email: '', displayName: '' },
  });

  useEffect(() => {
    if (detail) {
      form.reset({ email: detail.email, displayName: detail.displayName ?? '' });
    }
  }, [detail, form]);

  const handleClose = () => {
    setActiveTab('info');
    setIsEditing(false);
    setShowEditConfirm(false);
    setPendingEditValues(null);
    setShowRoleDialog(false);
    setShowToggleModal(false);
    setShowResendModal(false);
    form.reset({ email: '', displayName: '' });
    onClose();
  };

  const handleSwitchTab = (tab: DetailTab) => {
    if (tab !== 'info' && isEditing) handleCancelEdit();
    setActiveTab(tab);
  };

  const handleEditSubmit = (values: UpdateAdminUserFormValues) => {
    setPendingEditValues(values);
    setShowEditConfirm(true);
  };

  const handleEditConfirm = () => {
    if (!pendingEditValues || !user) return;
    updateUser(
      { adminUserId: user.id, data: pendingEditValues },
      {
        onSuccess: () => {
          setShowEditConfirm(false);
          setPendingEditValues(null);
          setIsEditing(false);
        },
        onError: () => setShowEditConfirm(false),
      },
    );
  };

  const handleCancelEdit = () => {
    if (detail) form.reset({ email: detail.email, displayName: detail.displayName ?? '' });
    setIsEditing(false);
  };

  const handleRoleConfirm = () => {
    if (!user) return;
    if (currentUserId === user.id) {
      Notification.warning({
        title: 'Acción no permitida',
        description: 'No puedes cambiar tu propio rol.',
      });
      setShowRoleDialog(false);
      return;
    }
    changeRole(
      { adminUserId: user.id, roleId: selectedRoleId },
      { onSuccess: () => setShowRoleDialog(false) },
    );
  };

  const handleToggleConfirm = (reason: string) => {
    if (!user) return;
    if (user.isActive) {
      deactivate({ adminUserId: user.id, reason }, { onSuccess: () => setShowToggleModal(false) });
    } else {
      activate({ adminUserId: user.id, reason }, { onSuccess: () => setShowToggleModal(false) });
    }
  };

  const handleResendConfirm = (email: string) => {
    if (!user) return;
    resend({ adminUserId: user.id, email }, { onSuccess: () => setShowResendModal(false) });
  };

  return (
    <>
      <ModalForm
        open={!!user}
        headerIcon={<User />}
        onOpenChange={handleClose}
        title="Detalle de usuario"
        description={'Información detallada sobre este usuario'}
        className="sm:max-w-[45rem]"
      >
        {isLoading && <div className="py-8" aria-hidden />}

        {isError && (
          <p className="text-sm text-negative py-4">Error al cargar el detalle del usuario.</p>
        )}

        {detail && user && (
          <>
            <div className="flex gap-1 border-b border-outline mb-4">
              {DETAIL_TABS.map(({ key, label, icon: Icon }) => (
                <UnifiedButton
                  key={key}
                  type="button"
                  onClick={() => handleSwitchTab(key)}
                  data-testid={`admin-users-detail-tab-${key}`}
                  className={cn(
                    'px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px',
                    activeTab === key
                      ? 'border-prominent text-light'
                      : 'border-transparent text-primary hover:text-light',
                  )}
                >
                  <Icon size={16} /> {label}
                </UnifiedButton>
              ))}
            </div>

            {activeTab === 'login-attempts' && <AdminUserLoginAttemptsTab adminUserId={user.id} />}

            {activeTab === 'info' && (
              <AdminUserInfoTab
                detail={detail}
                user={user}
                isEditing={isEditing}
                isUpdating={isUpdating}
                form={form}
                onEditSubmit={handleEditSubmit}
                onStartEdit={() => setIsEditing(true)}
                onCancelEdit={handleCancelEdit}
                onOpenToggle={() => setShowToggleModal(true)}
                onOpenRoleDialog={() => {
                  setSelectedRoleId(detail.roleId);
                  setShowRoleDialog(true);
                }}
                onOpenResend={() => setShowResendModal(true)}
              />
            )}
          </>
        )}
      </ModalForm>

      <ToggleStatusModal
        user={showToggleModal ? user : null}
        isPending={togglePending}
        onClose={() => setShowToggleModal(false)}
        onConfirm={handleToggleConfirm}
      />

      <ResendInvitationModal
        user={showResendModal ? user : null}
        isPending={resendPending}
        onClose={() => setShowResendModal(false)}
        onConfirm={handleResendConfirm}
      />

      <ConfirmDialog
        open={showEditConfirm}
        onOpenChange={(v) => {
          if (!isUpdating) setShowEditConfirm(v);
        }}
        title="Confirmar cambios"
        description={
          <span>
            Se actualizará el usuario con correo{' '}
            <span className="font-medium text-light">{pendingEditValues?.email}</span> y nombre{' '}
            <span className="font-medium text-light">{pendingEditValues?.displayName}</span>.
            {currentUserId === user?.id &&
              pendingEditValues?.email &&
              detail?.email &&
              pendingEditValues.email !== detail.email && (
                <span className="block mt-2 text-warn font-medium">
                  Cambiar tu correo cerrará tu sesión actual. Deberás iniciar sesión nuevamente.
                </span>
              )}
          </span>
        }
        onConfirm={handleEditConfirm}
        confirmLabel="Confirmar"
        confirmLoadingLabel="Guardando..."
        isPending={isUpdating}
        onCancel={() => setShowEditConfirm(false)}
      />

      <ConfirmDialog
        open={showRoleDialog}
        onOpenChange={(v) => {
          if (!isChangingRole) setShowRoleDialog(v);
        }}
        title="Cambiar rol"
        description={
          <span>
            Selecciona el nuevo rol para{' '}
            <span className="font-medium text-light">{user?.email}</span>.
          </span>
        }
        onConfirm={handleRoleConfirm}
        confirmLabel="Confirmar"
        confirmLoadingLabel="Guardando..."
        isPending={isChangingRole}
        onCancel={() => setShowRoleDialog(false)}
      >
        <SelectInput
          label="Rol"
          value={selectedRoleId}
          onValueChange={setSelectedRoleId}
          disabled={isChangingRole || rolesLoading}
          placeholder={rolesLoading ? 'Cargando roles...' : 'Seleccionar rol'}
        >
          {rolesData?.items.map((role) => (
            <SelectItem key={role.id} value={role.id}>
              {getRoleLabel(role.name)}
            </SelectItem>
          ))}
        </SelectInput>
      </ConfirmDialog>
    </>
  );
}
